import { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Label } from 'components/Typography';
import { useFormik } from 'formik';
import { parse, isValid, sub, format } from 'date-fns';
import { isTablet, isMobile } from 'react-device-detect';
import { FaCaretDown } from 'react-icons/fa';
import { DAYS, MONTHS as LONG_MONTHS, SHORT_MONTHS, YEARS } from './consts';
import validation from './validation';
import {
  Container,
  SelectContainer,
  DefaultSelectWrapper,
  StyledSelect,
  DisabledDate,
  ErrorMessage,
  CheckIcon
} from './styled';

const DateInput = ({
  variant,
  label,
  formik,
  name,
  showCaret = true,
  className,
  daysInPastLimit,
  availableDays = DAYS,
  availableYears = YEARS,
  disabled,
  forceAvailableDays = false,
  onMonthChange
}) => {
  const { setFieldValue, values } = formik;
  const MONTHS = variant === 'small' ? SHORT_MONTHS : LONG_MONTHS;

  const dateFormik = useFormik({
    initialValues: useMemo(
      () => ({
        month: '',
        day: '',
        year: ''
      }),
      []
    ),
    validationSchema: validation,
    validateOnMount: true
  });

  // Add effect to handle month changes
  useEffect(() => {
    const { month, day, year } = dateFormik.values;
    if (month && day && year) {
      const lastDayOfMonth = new Date(
        parseInt(year, 10),
        parseInt(month, 10),
        0
      ).getDate();
      const selectedDay = parseInt(day, 10);
      // Check if the selected day is within valid range (1 to lastDayOfMonth)
      if (selectedDay < 1 || selectedDay > lastDayOfMonth) {
        dateFormik.setFieldValue('day', '');
        // Also update the parent formik to clear the date
        setFieldValue(name, '');
      }
    }
    // Notify parent component of month change
    if (month && onMonthChange) {
      onMonthChange(month);
    }
  }, [
    dateFormik.values.month,
    dateFormik.values.year,
    onMonthChange,
    setFieldValue,
    name
  ]);

  const selectableDays = (selectedMonth, selectedYear) => {
    const monthIndex = (month) => (month > 0 ? month - 1 : MONTHS.length + month - 1);
    const getDays = (month) => {
      return month !== undefined && month !== null
        ? MONTHS[monthIndex(month)].days
        : DAYS;
    };

    const selectedMonthNum = Number(selectedMonth) || null;

    const currentMonth = new Date().getMonth() + 1;
    const currentDay = new Date().getDate();
    const cutoffYear = sub(new Date(), { days: daysInPastLimit }).getFullYear();
    const cutoffMonth = sub(new Date(), { days: daysInPastLimit }).getMonth() + 1;

    const earliestDay =
      currentDay > daysInPastLimit
        ? currentDay - daysInPastLimit
        : getDays(cutoffMonth).length - (daysInPastLimit - currentDay);

    if (Number(selectedYear) !== cutoffYear) {
      return getDays(selectedMonthNum);
    }

    const currentMonthCutoffMonthAndSelected =
      selectedMonthNum === cutoffMonth &&
      cutoffMonth === currentMonth &&
      currentDay > daysInPastLimit;

    const previousMonthCutoffMonthAndSelected =
      selectedMonthNum === cutoffMonth &&
      cutoffMonth !== currentMonth &&
      currentDay <= daysInPastLimit;

    if (currentMonthCutoffMonthAndSelected) {
      return getDays(selectedMonthNum).slice(
        earliestDay - 1,
        getDays(selectedMonthNum).length
      );
    }

    if (previousMonthCutoffMonthAndSelected) {
      return getDays(selectedMonthNum).slice(
        earliestDay - 1,
        getDays(selectedMonthNum).length
      );
    }

    return getDays(selectedMonthNum);
  };

  const selectableMonths = (selectedYear) => {
    const currentDay = new Date().getDate();
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const canCancelDaysInPreviousYear =
      currentMonth === 1 && currentDay < daysInPastLimit + 1;
    if (selectedYear === `${currentYear}` && !canCancelDaysInPreviousYear) {
      return currentDay < daysInPastLimit + 1
        ? MONTHS.slice(currentMonth - 2)
        : MONTHS.slice(currentMonth - 1);
    }
    if (Number(selectedYear) === currentYear - 1 && canCancelDaysInPreviousYear) {
      return [MONTHS[MONTHS.length - 1]];
    }
    return MONTHS;
  };

  useEffect(() => {
    const { year, month, day } = dateFormik.values;
    setFieldValue(name, year || month || day ? `${year}-${month}-${day}` : '');
  }, [dateFormik.values, setFieldValue, name]);

  useEffect(() => {
    const date = parse(values[name], 'yyyy-MM-dd', new Date());
    const isDateValid = isValid(date);

    if (isDateValid) {
      const parsedDate = values[name].split('-');
      dateFormik.setFieldValue('month', parsedDate[1]);
      dateFormik.setFieldValue('day', parsedDate[2]);
      dateFormik.setFieldValue('year', parsedDate[0]);
    }
  }, []); // eslint-disable-line

  // Even though Dan Abramov says you shouldn't disable the exhaustive-deps flag
  // (https://github.com/facebook/create-react-app/issues/6880#issuecomment-485912528)
  // it makes perfect sense to do it here because if you put values[name] as a
  // dependency it will check if the date is valid on every change which is
  // unnecessary. We only need to check this on mount.

  const renderLabel = (
    <Label htmlFor={name} variant={variant}>
      {label}
      {formik && label && (
        <>
          {dateFormik.month !== '' &&
          dateFormik.day !== '' &&
          dateFormik.year !== '' &&
          formik.errors[name] ? (
            <ErrorMessage>{formik.errors[name]}</ErrorMessage>
          ) : null}
          {!formik.errors[name] && formik.dirty && <CheckIcon />}
        </>
      )}
    </Label>
  );

  const onMobileSelectChange = (e) => {
    dateFormik.setFieldValue(e.target.name, e.target.value);
    dateFormik.setFieldTouched(e.target.name, true, true);
  };

  const renderMobileSelect = (
    <SelectContainer>
      <DefaultSelectWrapper variant={variant} isPlaceholder={!dateFormik.values.month}>
        {showCaret && <FaCaretDown />}
        <select
          onChange={onMobileSelectChange}
          value={dateFormik.values.month}
          name="month"
          disabled={disabled}
          defaultValue=""
        >
          <option value="" disabled hidden>
            Month
          </option>
          {(daysInPastLimit != null
            ? selectableMonths(dateFormik.values.year)
            : MONTHS
          ).map((month, index) => (
            <option key={index} value={month.value}>
              {month.label}
            </option>
          ))}
        </select>
      </DefaultSelectWrapper>
      <DefaultSelectWrapper variant={variant} isPlaceholder={!dateFormik.values.day}>
        {showCaret && <FaCaretDown />}
        <select
          onChange={onMobileSelectChange}
          name="day"
          value={dateFormik.values.day}
          disabled={disabled}
          defaultValue=""
        >
          <option value="" disabled hidden>
            Day
          </option>
          {(!forceAvailableDays && daysInPastLimit != null
            ? selectableDays(dateFormik.values.month, dateFormik.values.year)
            : availableDays
          ).map((day, index) => (
            <option key={index} value={day.value}>
              {day.label}
            </option>
          ))}
        </select>
      </DefaultSelectWrapper>
      <DefaultSelectWrapper variant={variant} isPlaceholder={!dateFormik.values.year}>
        {showCaret && <FaCaretDown />}
        <select
          onChange={onMobileSelectChange}
          name="year"
          value={dateFormik.values.year}
          disabled={disabled}
          defaultValue=""
        >
          <option value="" disabled hidden>
            Year
          </option>
          {availableYears.map((year, index) => (
            <option key={index} value={year.value}>
              {year.label}
            </option>
          ))}
        </select>
      </DefaultSelectWrapper>
    </SelectContainer>
  );

  const renderDesktopSelect = (
    <SelectContainer>
      {disabled ? (
        <DisabledDate>
          {format(
            new Date(
              dateFormik.values.year,
              Number(dateFormik.values.month) - 1,
              dateFormik.values.day
            ),
            'MMMM d, yyyy'
          )}
        </DisabledDate>
      ) : (
        <>
          <StyledSelect
            formik={dateFormik}
            options={
              daysInPastLimit != null ? selectableMonths(dateFormik.values.year) : MONTHS
            }
            name="month"
            placeholder="Month"
            disabled={disabled}
            variant={variant}
          />
          <StyledSelect
            options={
              !forceAvailableDays && daysInPastLimit != null
                ? selectableDays(dateFormik.values.month, dateFormik.values.year)
                : availableDays
            }
            formik={dateFormik}
            name="day"
            placeholder="Day"
            disabled={disabled}
            variant={variant}
          />
          <StyledSelect
            options={availableYears}
            formik={dateFormik}
            name="year"
            placeholder="Year"
            disabled={disabled}
            variant={variant}
          />
        </>
      )}
    </SelectContainer>
  );

  return (
    <Container className={className}>
      {renderLabel}

      {isTablet || isMobile ? renderMobileSelect : renderDesktopSelect}
    </Container>
  );
};

DateInput.propTypes = {
  variant: PropTypes.oneOf(['dark', 'light', 'small']),
  label: PropTypes.string,
  formik: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  showCaret: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  daysInPastLimit: PropTypes.number,
  availableDays: PropTypes.array,
  availableYears: PropTypes.array,
  forceAvailableDays: PropTypes.bool,
  onMonthChange: PropTypes.func
};

DateInput.defaultProps = {
  variant: 'dark',
  label: ''
};

export default DateInput;
